import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { skyDronePositions } from '../../../packages/domain/src/81-sky-projectiles.mjs';

/** Presentation of at most eight purchased escorts. Domain81 owns every muzzle
 * position, target and actual shot event. Imported immutable artwork stays owned
 * by113; this factory owns only baked geometry, material clones and GPU instances. */
export function createSkyEscorts(T, scene, models, { reduced = () => false } = {}) {
  const resources = new Set();
  const batches = [];
  const group = new T.Group();
  group.name = 'Sky purchased escort wing';
  let disposed = false;
  const own = (resource) => {
    resources.add(resource);
    return resource;
  };
  const batch = (geometry, material, name) => {
    const mesh = new T.InstancedMesh(geometry, material, 8);
    mesh.name = name;
    mesh.count = 0;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(T.DynamicDrawUsage);
    group.add(mesh);
    return mesh;
  };
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    scene.remove(group);
    for (const child of group.children) child.dispose();
    for (const resource of resources) resource.dispose();
    resources.clear();
    group.clear();
  };
  let bodyLength;
  try {
    const source = models.drone.scene;
    source.updateMatrixWorld(true);
    const bounds = new T.Box3().setFromObject(source);
    const size = bounds.getSize(new T.Vector3());
    const center = bounds.getCenter(new T.Vector3());
    const scale = 0.72 / Math.max(0.01, size.x);
    bodyLength = size.z * scale;
    const materials = new Map();
    source.traverse((node) => {
      if (!node.isMesh) return;
      const geometry = own(node.geometry.clone());
      geometry.applyMatrix4(node.matrixWorld);
      geometry.translate(-center.x, -center.y, -bounds.max.z);
      geometry.rotateY(Math.PI);
      geometry.scale(scale, scale, scale);
      const list = materials.get(node.material) ?? [];
      list.push(geometry);
      materials.set(node.material, list);
    });
    for (const [sourceMaterial, parts] of materials) {
      // Blender lofts carry UVs while solid trim may not; retain the authored
      // UVs and supply unused zero coordinates for the untextured trim.
      if (parts.some((part) => part.hasAttribute('uv')))
        for (const part of parts)
          if (!part.hasAttribute('uv'))
            part.setAttribute(
              'uv',
              new T.Float32BufferAttribute(new Float32Array(part.attributes.position.count * 2), 2),
            );
      let geometry;
      try {
        geometry = mergeGeometries(parts, false);
      } finally {
        for (const part of parts) {
          resources.delete(part);
          part.dispose();
        }
      }
      if (!geometry) throw new Error('Escort artwork could not be batched.');
      own(geometry);
      const material = own(sourceMaterial.clone());
      // Friendly ivory/blue trim stays distinct from hostile amber apertures.
      if (/gold cockpit|amber aperture/.test(material.name)) {
        material.color.set('#65dfff');
        material.emissive?.set('#1d85bd');
        material.emissiveIntensity = 0.7;
      }
      if (/petrol alloy/.test(material.name)) material.color.set('#25475b');
      material.name = `escort ${sourceMaterial.name}`;
      batches.push(batch(geometry, material, 'Original escort airframe'));
    }
    const ion = own(
      new T.MeshBasicMaterial({
        color: '#7beaff',
        transparent: true,
        opacity: 0.5,
        blending: T.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    const trailGeometry = own(new T.CylinderGeometry(0.008, 0.035, 0.7, 6, 1, true));
    trailGeometry.rotateX(Math.PI / 2);
    trailGeometry.translate(0, 0, 0.35);
    const trails = batch(trailGeometry, ion, 'Escort ion trails');
    const flashGeometry = own(new T.IcosahedronGeometry(0.075, 1));
    const flashes = batch(flashGeometry, ion, 'Actual escort muzzle flashes');
    const barrelGeometry = own(new T.CylinderGeometry(0.028, 0.045, 0.18, 8));
    barrelGeometry.rotateX(Math.PI / 2);
    barrelGeometry.translate(0, 0, 0.09);
    const barrelMaterial = own(
      new T.MeshStandardMaterial({
        color: '#b7d4d7',
        roughness: 0.35,
        metalness: 0.7,
      }),
    );
    const barrels = batch(barrelGeometry, barrelMaterial, 'Escort gimballed cannon');
    const pose = new T.Object3D();
    const forward = new T.Vector3(0, 0, -1);
    const direction = new T.Vector3();
    const aim = new T.Quaternion();
    const roll = new T.Quaternion();
    const rollAxis = new T.Vector3(0, 0, 1);
    scene.add(group);
    return {
      group,
      draw(state) {
        if (state.traversal) {
          group.visible = false;
          return;
        }
        if (disposed) return;
        const positions = skyDronePositions(state).slice(0, 8);
        const quiet = reduced();
        let flashCount = 0;
        for (const [index, point] of positions.entries()) {
          const drone = state.drones?.find((item) => item.index === point.index);
          direction.set(
            (drone?.aimX ?? point.x) - point.x,
            (drone?.aimY ?? point.y) - point.y,
            (drone?.aimZ ?? point.z - 30) - point.z,
          );
          if (direction.lengthSq() < 0.001) direction.copy(forward);
          direction.normalize();
          aim.setFromUnitVectors(forward, direction);
          pose.position.set(point.x, point.y, point.z);
          pose.scale.setScalar(1);
          pose.quaternion.copy(aim);
          pose.updateMatrix();
          barrels.setMatrixAt(index, pose.matrix);
          if (!quiet) {
            roll.setFromAxisAngle(rollAxis, Math.sin(state.time * 2.2 + point.index) * 0.055);
            pose.quaternion.multiply(roll);
          }
          pose.updateMatrix();
          for (const mesh of batches) mesh.setMatrixAt(index, pose.matrix);
          pose.translateZ(bodyLength * 0.9);
          pose.scale.set(1, 1, quiet ? 0.22 : 0.75 + 0.15 * Math.sin(state.time * 9 + point.index));
          pose.updateMatrix();
          trails.setMatrixAt(index, pose.matrix);
          const shotAge = state.time - (drone?.lastShotAt ?? -Infinity);
          if (!quiet && shotAge >= 0 && shotAge < 0.075) {
            pose.position.set(point.x, point.y, point.z);
            pose.scale.setScalar(1 - shotAge / 0.09);
            pose.updateMatrix();
            flashes.setMatrixAt(flashCount++, pose.matrix);
          }
        }
        group.visible = positions.length > 0;
        for (const mesh of [...batches, trails, barrels]) {
          mesh.count = positions.length;
          mesh.instanceMatrix.needsUpdate = true;
        }
        flashes.count = flashCount;
        flashes.instanceMatrix.needsUpdate = true;
      },
      dispose,
    };
  } catch (error) {
    dispose();
    throw error;
  }
}
